import React, { useState, useMemo } from "react";

/**
 * LogoWithFallback — renders the first URL that loads. If every URL 404s
 * (or the caller passes no URLs at all), it renders a colored disc with
 * the letter fallback so we never show a broken image icon.
 *
 * `urls`   — ordered fallback chain (SimpleIcons → Clearbit → domain favicon).
 * `letter` — single-character fallback (usually the first letter of the name).
 * `color`  — background for the letter disc.
 */
export function LogoWithFallback({
  urls = [],
  letter = "?",
  color = "#0B1220",
  size = 20,
  round = true,
  bare = false,      // if true: raw <img>, no white disc, no border/shadow container
  className = "",
  alt = "",
  ...rest
}) {
  const chain = useMemo(() => urls.filter(Boolean), [urls]);
  const [idx, setIdx] = useState(0);
  const src = chain[idx];
  const containerStyle = {
    width: size,
    height: size,
    borderRadius: round ? "50%" : 6,
    overflow: "hidden",
    flexShrink: 0,
  };

  // Bare mode: return the image directly with no white disc / no border.
  // If every URL fails we still fall back to a small colored initial glyph
  // so we never emit a broken-image icon.
  if (bare) {
    if (src) {
      return (
        <img
          src={src}
          alt={alt}
          onError={() => setIdx((i) => i + 1)}
          style={{ width: size, height: size, objectFit: "contain", display: "inline-block", flexShrink: 0 }}
          className={className}
          {...rest}
        />
      );
    }
    return (
      <span
        className={`inline-flex items-center justify-center font-bold ${className}`}
        style={{
          width: size,
          height: size,
          fontSize: Math.max(9, Math.round(size * 0.6)),
          color,
          flexShrink: 0,
        }}
        {...rest}
      >
        {letter}
      </span>
    );
  }

  if (src) {
    return (
      <span className={`inline-flex items-center justify-center bg-white ${className}`} style={containerStyle} {...rest}>
        <img
          src={src}
          alt={alt}
          onError={() => setIdx((i) => i + 1)}
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
        />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center justify-center text-white font-bold ${className}`}
      style={{
        ...containerStyle,
        background: color,
        fontSize: Math.max(9, Math.round(size * 0.55)),
      }}
      {...rest}
    >
      {letter}
    </span>
  );
}

/**
 * toolLogoUrls — pull the best-known real-logo URL chain out of a tool
 * document from the API. Every tool has at least a Clearbit URL; some
 * also have an explicit `url`, so we synthesize a Google-favicon URL
 * from its hostname as a second fallback.
 */
export function toolLogoUrls(tool) {
  const urls = [];
  if (tool?.logo_url) urls.push(tool.logo_url);
  try {
    if (tool?.url) {
      const host = new URL(tool.url).host;
      if (host) {
        // Google's favicon service is essentially bulletproof.
        urls.push(`https://www.google.com/s2/favicons?domain=${host}&sz=128`);
      }
    }
  } catch { /* invalid URL — skip */ }
  return urls;
}
